import React from 'react';

interface UseFloatingWhenOffscreenResult {
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  isFloating: boolean;
  isFloatingEnabled: boolean;
  spacerHeight: number;
}

function isFloatingSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.addEventListener === 'function' &&
    typeof Element !== 'undefined' &&
    typeof Element.prototype.getBoundingClientRect === 'function'
  );
}

export function useFloatingWhenOffscreen(
  resetKey: unknown
): UseFloatingWhenOffscreenResult {
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  const isFloatingEnabled = isFloatingSupported();
  const [isFloating, setIsFloating] = React.useState(false);
  const [spacerHeight, setSpacerHeight] = React.useState(0);

  React.useEffect(() => {
    if (!isFloatingEnabled) {
      return undefined;
    }

    const element = sentinelRef.current;
    if (!element) {
      return undefined;
    }

    const update = () => {
      try {
        const { bottom } = element.getBoundingClientRect();
        const shouldFloat = bottom > window.innerHeight;

        if (element.offsetHeight > 0) {
          setSpacerHeight(element.offsetHeight);
        }

        setIsFloating(shouldFloat);
      } catch {
        setIsFloating(false);
      }
    };

    update();

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(element);
    }

    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      resizeObserver?.disconnect();
    };
  }, [isFloatingEnabled, resetKey]);

  return {
    sentinelRef,
    isFloating: isFloatingEnabled && isFloating,
    isFloatingEnabled,
    spacerHeight,
  };
}
